/**
 * 快速指南組件 | Quick Guide Component
 * 提供新使用者快速上手指引
 * 注重資訊層級與視覺引導
 * 支援 localStorage 持久化與新使用者檢測
 */

import { useState, useEffect } from 'react';

interface GuideStep {
  number: number;
  title: string;
  description: string;
  action: string;
  icon: string;
  tab?: string;
}

const GUIDE_STEPS: GuideStep[] = [
  {
    number: 1,
    title: '建立訂單',
    description: '開始建立您的第一個生產訂單，定義產品、數量和交期',
    action: '前往訂單管理',
    icon: '📦',
    tab: 'orders',
  },
  {
    number: 2,
    title: '查看甘特圖',
    description: '使用甘特圖視圖了解工單排程與資源分配',
    action: '查看工單甘特圖',
    icon: '📊',
    tab: 'gantt',
  },
  {
    number: 3,
    title: '管理插單',
    description: '處理緊急訂單插單需求，模擬並分析影響範圍',
    action: '前往插單管理',
    icon: '⚡',
    tab: 'rush',
  },
  {
    number: 4,
    title: '生產報工',
    description: '記錄生產進度與產出數量，追蹤實際生產狀況',
    action: '前往生產報工',
    icon: '📝',
    tab: 'report',
  },
];

const STORAGE_KEY = 'quickguide_dismissed';

// 輔助函數：重置指南狀態（供外部使用）
export function resetQuickGuide() {
  try {
    localStorage.removeItem(STORAGE_KEY);
    console.log('快速指南已重置，下次造訪將重新顯示');
  } catch (error) {
    console.warn('無法重置快速指南狀態:', error);
  }
}

interface QuickGuideProps {
  onNavigate?: (tab: string) => void;
  onClose?: () => void;
  showForNewUser?: boolean;  // 新增：是否為新使用者模式
}

export function QuickGuide({ onNavigate, onClose, showForNewUser = false }: QuickGuideProps) {
  const [currentStep, setCurrentStep] = useState(0);
  const [isDismissed, setIsDismissed] = useState(false);
  const [dontShowAgain, setDontShowAgain] = useState(false);

  // 從 localStorage 讀取關閉狀態
  useEffect(() => {
    try {
      const dismissed = localStorage.getItem(STORAGE_KEY);
      if (dismissed === 'true') {
        setIsDismissed(true);
      }
    } catch (error) {
      console.warn('無法讀取快速指南狀態:', error);
    }
  }, []);

  if (isDismissed) return null;

  const handleNext = () => {
    if (currentStep < GUIDE_STEPS.length - 1) {
      setCurrentStep(currentStep + 1);
    } else {
      handleComplete();
    }
  };

  const handlePrevious = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleComplete = () => {
    // 儲存到 localStorage
    if (dontShowAgain) {
      try {
        localStorage.setItem(STORAGE_KEY, 'true');
      } catch (error) {
        console.warn('無法儲存快速指南狀態:', error);
      }
    }
    setIsDismissed(true);
    onClose?.();
  };

  const handleAction = () => {
    const step = GUIDE_STEPS[currentStep];
    if (step.tab && onNavigate) {
      setIsDismissed(true);
      onNavigate(step.tab);
    }
  };

  const step = GUIDE_STEPS[currentStep];
  const progress = ((currentStep + 1) / GUIDE_STEPS.length) * 100;

  return (
    <div className="bg-gradient-to-br from-blue-50 to-indigo-50 border-2 border-blue-200 rounded-2xl p-6 shadow-lg mb-6 animate-fade-in">
      {/* Header */}
      <div className="flex items-start justify-between mb-4">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl flex items-center justify-center shadow-lg shadow-blue-500/30">
            <span className="text-white text-lg">💡</span>
          </div>
          <div>
            <h3 className="text-lg font-bold text-slate-800">快速入門指南</h3>
            <p className="text-sm text-slate-600">了解如何使用製造排程系統</p>
          </div>
        </div>
        <button
          onClick={handleComplete}
          className="text-slate-400 hover:text-slate-600 transition-colors p-1 hover:bg-slate-200/50 rounded-lg"
          aria-label="關閉指南"
        >
          <span className="text-xl">×</span>
        </button>
      </div>

      {/* Progress Bar */}
      <div className="mb-6">
        <div className="flex items-center justify-between text-xs text-slate-600 mb-2">
          <span>步驟 {currentStep + 1} / {GUIDE_STEPS.length}</span>
          <span>{Math.round(progress)}% 完成</span>
        </div>
        <div className="h-2 bg-blue-100 rounded-full overflow-hidden">
          <div
            className="h-full bg-gradient-to-r from-blue-500 to-indigo-500 transition-all duration-500 ease-out"
            style={{ width: `${progress}%` }}
          />
        </div>
      </div>

      {/* Step Content */}
      <div className="bg-white border-2 border-white/50 rounded-xl p-5 shadow-sm mb-5">
        <div className="flex items-start gap-4">
          {/* Step Number */}
          <div className="flex-shrink-0 w-12 h-12 bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl flex items-center justify-center shadow-lg shadow-blue-500/30">
            <span className="text-white text-2xl font-black">{step.number}</span>
          </div>

          {/* Content */}
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-2">
              <span className="text-3xl">{step.icon}</span>
              <h4 className="text-xl font-bold text-slate-800">{step.title}</h4>
            </div>
            <p className="text-slate-600 leading-relaxed mb-3">{step.description}</p>

            {/* Step Indicators */}
            <div className="flex items-center gap-2">
              {GUIDE_STEPS.map((s, index) => (
                <div
                  key={s.number}
                  className={`h-1.5 rounded-full transition-all duration-300 ${
                    index === currentStep
                      ? 'w-8 bg-blue-500'
                      : index < currentStep
                      ? 'w-2 bg-green-500'
                      : 'w-2 bg-slate-200'
                  }`}
                />
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Actions */}
      <div className="flex items-center justify-between">
        <button
          onClick={handlePrevious}
          disabled={currentStep === 0}
          className={`px-4 py-2 rounded-lg text-sm font-semibold transition-all ${
            currentStep === 0
              ? 'bg-slate-100 text-slate-400 cursor-not-allowed'
              : 'bg-slate-100 text-slate-700 hover:bg-slate-200 active:scale-95'
          }`}
        >
          ← 上一步
        </button>

        <div className="flex gap-3">
          <button
            onClick={handleAction}
            className="px-5 py-2.5 bg-gradient-to-r from-blue-600 to-blue-500 hover:from-blue-700 hover:to-blue-600 text-white rounded-lg transition-all text-sm font-semibold shadow-lg shadow-blue-500/30 hover:shadow-xl hover:shadow-blue-500/40 active:scale-95"
          >
            {step.action}
          </button>
          <button
            onClick={handleNext}
            className="px-5 py-2.5 bg-gradient-to-r from-indigo-600 to-indigo-500 hover:from-indigo-700 hover:to-indigo-600 text-white rounded-lg transition-all text-sm font-semibold shadow-lg shadow-indigo-500/30 hover:shadow-xl hover:shadow-indigo-500/40 active:scale-95"
          >
            {currentStep === GUIDE_STEPS.length - 1 ? '完成' : '下一步 →'}
          </button>
        </div>
      </div>

      {/* "Don't Show Again" Checkbox */}
      <div className="flex items-center gap-2 mt-3 pt-3 border-t border-blue-200">
        <input
          type="checkbox"
          id="dont-show-again"
          checked={dontShowAgain}
          onChange={(e) => setDontShowAgain(e.target.checked)}
          className="w-4 h-4 text-blue-600 border-blue-300 rounded focus:ring-blue-500 cursor-pointer"
        />
        <label htmlFor="dont-show-again" className="text-sm text-slate-700 cursor-pointer select-none">
          不再顯示此指南
        </label>
      </div>
    </div>
  );
}

// 迷你版快速提示卡片（用於側邊欄或底部）
export function QuickTipsCard({ onNavigate }: { onNavigate?: (tab: string) => void }) {
  const tips = [
    { icon: '💡', text: '點擊統計卡片可快速導航到相關頁面', tab: 'overview' },
    { icon: '⌨️', text: '使用 Tab 鍵可在導航選項之間快速切換', tab: 'overview' },
    { icon: '📊', text: '甘特圖支援拖曳調整工單時間', tab: 'gantt' },
  ];

  return (
    <div className="bg-gradient-to-br from-amber-50 to-orange-50 border-2 border-amber-200 rounded-xl p-4 shadow-sm">
      <div className="flex items-center gap-2 mb-3">
        <span className="text-xl">💡</span>
        <h4 className="font-bold text-amber-800">快速提示</h4>
      </div>
      <ul className="space-y-2">
        {tips.map((tip, index) => (
          <li key={index} className="flex items-start gap-2 text-sm text-amber-900">
            <span>{tip.icon}</span>
            <span>{tip.text}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

// 區段標題組件 - 改善資訊層級
interface SectionHeaderProps {
  title: string;
  description?: string;
  icon?: string;
  action?: { label: string; onClick: () => void };
  variant?: 'default' | 'compact' | 'with-bg';
}

export function SectionHeader({
  title,
  description,
  icon,
  action,
  variant = 'default'
}: SectionHeaderProps) {
  const baseClasses = "flex items-center justify-between";
  const titleClasses = {
    default: 'text-xl font-bold text-slate-800',
    compact: 'text-lg font-bold text-slate-800',
    'with-bg': 'text-lg font-bold text-white'
  }[variant];

  const bgClasses = {
    default: '',
    compact: '',
    'with-bg': 'bg-gradient-to-r from-slate-700 to-slate-800 -mx-6 -mt-6 px-6 pt-6 pb-4 rounded-t-xl'
  }[variant];

  return (
    <div className={`${baseClasses} ${bgClasses} mb-4`}>
      <div className="flex items-center gap-3">
        {icon && (
          <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${
            variant === 'with-bg'
              ? 'bg-white/10'
              : 'bg-gradient-to-br from-slate-100 to-slate-200'
          }`}>
            <span className="text-lg">{icon}</span>
          </div>
        )}
        <div>
          <h3 className={titleClasses}>{title}</h3>
          {description && (
            <p className={`text-sm ${
              variant === 'with-bg' ? 'text-slate-300' : 'text-slate-600'
            } mt-0.5`}>
              {description}
            </p>
          )}
        </div>
      </div>
      {action && (
        <button
          onClick={action.onClick}
          className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm font-semibold transition-all shadow-md shadow-blue-500/20 hover:shadow-lg active:scale-95"
        >
          {action.label}
        </button>
      )}
    </div>
  );
}
